import {Navigate} from 'react-router-dom';
import {useAppOverrides} from 'contexts/AppOverridesContext';
import {HomeRedirect} from 'course/splash/HomeRedirect';

/**
 * Route element for `/:courseId/home`.
 *
 * Reads {@link AppOverrides.CourseHomePage} from {@link AppOverridesContext}:
 * - When an override is provided, renders it directly.
 * - Otherwise, renders the default CIP {@link HomeRedirect}, which redirects
 *   to a role-specific home (e.g. `studenthome`).
 *
 * This is the single touch-point that lets external deploy wrappers (e.g. PAI)
 * substitute a custom course-home page without editing any CIP source file.
 *
 * @return The override page, or the role-based redirect.
 */
export function CourseHomeRoute() {
  const {CourseHomePage} = useAppOverrides();
  if (CourseHomePage) {
    return <CourseHomePage />;
  }
  return <HomeRedirect />;
}

/**
 * Route element for the bare `/:courseId` index.
 *
 * Renders {@link AppOverrides.CourseHomePage} when provided; otherwise
 * forwards to `home`. The index route sits outside `CoursePage`, so it has no
 * `ProfileProvider` and {@link HomeRedirect} would see no course role here.
 *
 * @return The override page, or a redirect to `/:courseId/home`.
 */
export function CourseIndexRoute() {
  const {CourseHomePage} = useAppOverrides();
  if (CourseHomePage) {
    return <CourseHomePage />;
  }
  return <Navigate to="home" replace />;
}
